/**
 * RecordActions — universal record-page action row (Push #36 lock;
 * cap tightened to 2 in Push 4-1af 2026-07-10; back-nav removed in
 * Push 4-1ao 2026-07-11).
 *
 * THE RULE (formulaic, not whackamole):
 *   At most 2 actions render inline. Any 3rd+ action automatically collapses
 *   into a "More ▾" overflow menu (ActionOverflow). This is a structural
 *   guarantee — every record page that uses this component gets the rule for
 *   free. There is no opt-out.
 *
 *   Push 4-1af (2026-07-10) tightened the cap from 3 to 2 per Ean: highlight
 *   panels should surface at most 2 visible actions; overflow menu now
 *   already works well, so we lean on it earlier. Applies universally —
 *   record detail pages AND any other highlight-panel action cluster.
 *
 * Push 4-1ao (2026-07-11): the `back` prop was DELETED. Record-detail pages
 * no longer surface a "back to X" affordance in the action row. Rationale:
 * browser back is universally understood; a back-nav pill inside the action
 * cluster consumed weight + fought the highlight panel's spacing. Every
 * callsite that previously passed `back={{ to, label }}` had that prop
 * removed. If a future record-detail page needs contextual up-nav, use the
 * <Breadcrumb> primitive rendered ABOVE the highlight panel — never inside
 * the action row. See PATTERNS.md "No back-nav on record detail".
 *
 * Slot order (left → right):
 *   1. Inline secondary (max 1) — in array order, outlined button.
 *   2. Primary action (max 1) — filled teal, rightmost inline slot.
 *   3. Overflow "More ▾" — everything that didn't fit.
 *
 * Hidden actions (visibility: false) are filtered out BEFORE counting, so
 * conditional Clone/Share rows don't push other actions into overflow
 * unnecessarily.
 *
 * Usage:
 *   <RecordActions
 *     actions={[
 *       { label: 'Clone', onClick: handleClone, visible: canClone },
 *       { label: 'Share', onClick: handleShare, visible: canShare },
 *       { label: 'Copy prompt', copy: { value: body } },
 *       { label: 'Use in chat', to: '/chat', primary: true },
 *     ]}
 *   />
 *
 * Action shapes (one of `onClick`, `to`, or `copy` is required):
 *   - `onClick: () => void` — regular button
 *   - `to: string`         — router link (Foundation Button asChild + RouterNavLink)
 *   - `copy: { value, successMessage? }` — copy-to-clipboard (Foundation Button + toast)
 *
 * Why this is its own component instead of a tweak to ActionOverflow:
 *   ActionOverflow only renders the "More ▾" leaf. The crowding problem is
 *   that pages were hand-rolling the action row above it. Centralizing the
 *   3-inline-rest-overflow split at this level means a page can never again
 *   ship a 5-button row by accident.
 */

import { Button } from '@olytic/foundation';
import type { ReactNode } from 'react';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard.ts';
import { RouterNavLink } from '../lib/foundationLinks.tsx';
import { ActionOverflow, type ActionOverflowItem } from './ActionOverflow.tsx';
import { ButtonCluster } from './ButtonCluster.tsx';
import { IconCopy } from './Icon.tsx';

// Push 4-1af (2026-07-10): cap tightened from 3 → 2 per Ean universal
// highlight-panel action-cap rule. Applies to every RecordActions callsite;
// no per-page opt-out.
const MAX_INLINE_ACTIONS = 2;

export interface RecordAction {
  /** Button label. Required. */
  label: string;
  /** Pending label (shown while `pending` is true). Defaults to label + "…". */
  pendingLabel?: string;
  /** Hidden = excluded entirely (e.g., conditional Clone/Share). */
  visible?: boolean;
  /** Greyed out + non-clickable. */
  disabled?: boolean;
  /** While pending, button shows pendingLabel and is non-clickable. */
  pending?: boolean;
  /** Destructive — red treatment in overflow, danger border inline. */
  danger?: boolean;
  /** Native + aria title for hover hint. */
  title?: string;
  /**
   * Primary marker — exactly one action across the array can set this true.
   * Renders filled teal and is pinned to the rightmost inline slot.
   */
  primary?: boolean;
  // -------- one of these is required --------
  /** Plain button. */
  onClick?: () => void;
  /** Router link target — renders as a Button asChild around <RouterNavLink href=...>. */
  to?: string;
  /** Copy-to-clipboard variant. Renders a Foundation Button that copies and toasts. */
  copy?: { value: string; successMessage?: string };
}

interface RecordActionsProps {
  actions: RecordAction[];
  /**
   * Escape hatch for custom action components that don't fit the descriptor
   * shape (e.g., LibraryDetail's PrimaryAction which picks one of five CTAs
   * based on tenant install state). Rendered at the END of the action group
   * (rightmost). Use sparingly — prefer adding to `actions[]` so the rule
   * applies. The 3-inline limit does NOT count `extra`; treat it as a
   * single visually-pinned slot a page owns explicitly.
   */
  extra?: ReactNode;
  // Push 4-1ao (2026-07-11): the `back` prop was deleted. Record-detail
  // pages no longer surface a "back to X" affordance inside the action row.
  // See file-top doc block.
}

export function RecordActions({ actions, extra }: RecordActionsProps) {
  const copy = useCopyToClipboard();

  // Step 1 — drop hidden actions before any counting happens.
  const visible = actions.filter((a) => a.visible !== false);

  // Step 2 — separate primary (max 1) from secondaries while preserving order.
  const primary = visible.find((a) => a.primary === true) ?? null;
  const secondaries = visible.filter((a) => a.primary !== true);

  // Step 3 — apply the rule. We reserve 1 slot for primary if present, so
  // inline secondaries can be at most MAX_INLINE_ACTIONS - 1 = 1 (Push 4-1af).
  const inlineSecondaryBudget = primary === null ? MAX_INLINE_ACTIONS : MAX_INLINE_ACTIONS - 1;
  const inlineSecondaries = secondaries.slice(0, inlineSecondaryBudget);
  const overflowActions = secondaries.slice(inlineSecondaryBudget);

  // Step 4 — convert overflow actions into ActionOverflowItem shape.
  // Note: ActionOverflow's items only support onClick. For `to` links and
  // `copy` actions in overflow, we synthesize an onClick that navigates or
  // copies. This keeps the overflow menu consistent (no nested copy button).
  // `exactOptionalPropertyTypes: true` in tsconfig means we can't pass
  // `undefined` for optional bool/string props — must omit them entirely.
  // Build each item with conditional spreads so only set keys get through.
  const overflowItems: ActionOverflowItem[] = overflowActions.map((a) => {
    const item: ActionOverflowItem = {
      label: a.label,
      onClick: makeOverflowHandler(a),
    };
    if (a.danger === true) item.danger = true;
    if (a.disabled === true || a.pending === true) item.disabled = true;
    if (a.title !== undefined) item.title = a.title;
    return item;
  });

  return (
    <div className="record-actions">
      {/* Push 4-1ao (2026-07-11): the back-nav Link rendering block was
         deleted here. Record-detail pages no longer surface a "back to X"
         pill in the action cluster — browser back button is the universal
         affordance. See PATTERNS.md "No back-nav on record detail". */}
      {/* Push 4-1g-hardaudit (2026-07-08): the action group now renders via
         <ButtonCluster>, replacing the .record-actions-group CSS-selector
         patchwork with structural uniform-envelope enforcement. Every child
         (inline secondaries, primary, overflow trigger, extra) inherits the
         same 34px pill regardless of variant. .record-actions-group class
         retained for backward-compat with any legacy selectors + so the
         pattern's discoverable via grep. */}
      <ButtonCluster alignment="end" className="record-actions-group">
        {inlineSecondaries.map((a, i) => renderInline(a, `s-${i}`, copy))}
        {primary !== null && renderInline(primary, 'primary', copy)}
        {overflowItems.length > 0 && <ActionOverflow items={overflowItems} />}
        {extra !== undefined && extra}
      </ButtonCluster>
    </div>
  );
}

function renderInline(
  action: RecordAction,
  key: string,
  copy: ReturnType<typeof useCopyToClipboard>,
) {
  // Variant precedence (Foundation Button, PLAT-307; size "sm" like the copy
  // action beside it):
  //   primary  → brand       (filled — the one CTA)
  //   danger   → destructive (destructive secondary)
  //   default  → outline     (neutral secondary)
  // Primary outranks danger because primary is structurally singular (one per
  // row); a primary-danger combo would be a UX smell (the prominent CTA
  // shouldn't be the destructive one — hoist that to a confirm dialog).
  const variant =
    action.primary === true ? 'brand' : action.danger === true ? 'destructive' : 'outline';
  const label =
    action.pending === true ? (action.pendingLabel ?? `${action.label}…`) : action.label;
  const isDisabled = action.disabled === true || action.pending === true;

  if (action.copy !== undefined) {
    const { value, successMessage } = action.copy;
    return (
      <Button key={key} type="button" size="sm" onClick={() => copy(value, successMessage)}>
        <IconCopy size={14} />
        {action.label}
      </Button>
    );
  }

  if (action.to !== undefined) {
    return (
      <Button key={key} asChild variant={variant} size="sm">
        <RouterNavLink href={action.to} title={action.title ?? ''}>
          {label}
        </RouterNavLink>
      </Button>
    );
  }

  return (
    <Button
      key={key}
      type="button"
      variant={variant}
      size="sm"
      onClick={action.onClick}
      disabled={isDisabled}
      title={action.title ?? ''}
    >
      {label}
    </Button>
  );
}

function makeOverflowHandler(action: RecordAction): () => void {
  if (action.onClick !== undefined) return action.onClick;
  if (action.to !== undefined) {
    return () => {
      // Use window.location for overflow Link items — react-router's Link
      // requires being IN the JSX tree, not invoked from a callback. This is
      // the trade-off for unifying overflow items behind a single onClick
      // contract. Same-origin navigation; no full reload because Cloudflare
      // Pages SPA handles client-side routing on push.
      window.location.assign(action.to as string);
    };
  }
  if (action.copy !== undefined) {
    return () => {
      void navigator.clipboard.writeText(action.copy!.value);
    };
  }
  return () => undefined;
}
